/**
 * The headline: the latest week's score as the one figure, its movement
 * against the week before, the interval drawn on the −100 to +100 axis, and
 * the reading basis — how many comments were read, over which period. The
 * scoring method stays reachable through the estimate label's hint.
 */

import type { ReactNode } from 'react';

import { getLocale } from '../../../i18n';
import { count, interval, score } from '../../../chrome/measure/format';
import { EstimateLabel } from '../../../chrome/measure';
import { mt } from '../../../chrome/measure/messages';
import { DefinitionRows } from '../../../chrome/page';
import type { Window } from '../api/queries';
import { st2, st2Plural } from './messages';
import { SentimentCard } from './SentimentCard';
import { scoreTone } from './RegionCard';
import { movementOf, type LevelSeries } from './series';

const AXIS_SPAN = 200;

function axisPercent(value: number): number {
  return Math.min(Math.max(((value + 100) / AXIS_SPAN) * 100, 0), 100);
}

function IntervalTrack(props: { low: number; high: number; fill: string }): ReactNode {
  const left = axisPercent(props.low);
  const right = axisPercent(props.high);
  const range = mt('ncs.interval', { interval: interval(props.low, props.high, getLocale()) });
  return (
    <div className="flex flex-col gap-1">
      <div
        role="img"
        aria-label={`${st2('headline.range')}: ${range}`}
        className="relative h-1.5 rounded-pill bg-secondary"
      >
        <div
          data-slot="interval"
          className={`absolute inset-y-0 rounded-pill ${props.fill}`}
          style={{ left: `${left}%`, width: `${right - left}%` }}
        />
      </div>
      <div
        aria-hidden
        data-numeric
        className="text-caption text-muted-foreground flex justify-between gap-2"
      >
        <span>{st2('headline.axisMin')}</span>
        <span>{range}</span>
        <span>{st2('headline.axisMax')}</span>
      </div>
    </div>
  );
}

function Movement(props: { delta: number }): ReactNode {
  const points = Math.round(Math.abs(props.delta));
  if (points === 0) {
    return <p className="text-caption text-muted-foreground">{st2('headline.steady')}</p>;
  }
  const fell = props.delta < 0;
  return (
    <p data-numeric className={`text-caption ${fell ? 'text-destructive' : 'text-primary-ink'}`}>
      {st2Plural(fell ? 'headline.fell.other' : 'headline.rose.other', points, {
        n: count(points, getLocale()),
      })}
    </p>
  );
}

function day(iso: string): string {
  return new Intl.DateTimeFormat(getLocale(), { day: '2-digit', month: '2-digit' }).format(
    new Date(iso),
  );
}

export function LatestPoint(props: { series: LevelSeries; window: Window }): ReactNode {
  const latest = props.series.points.at(-1);
  if (latest === undefined) return null;
  const delta = movementOf(props.series);
  const tone = scoreTone(latest.score);

  return (
    <SentimentCard title={st2('headline.title')}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span data-numeric className={`text-display-md ${tone.text}`}>
          {score(latest.score, getLocale())}
        </span>
        <div className="flex flex-col gap-0.5">
          <p className="text-caption text-muted-foreground">{st2('headline.caption')}</p>
          {delta !== null && <Movement delta={delta} />}
        </div>
      </div>
      <IntervalTrack low={latest.low} high={latest.high} fill={tone.fill} />
      <section aria-label={st2('headline.basis')} className="rounded-lg bg-secondary px-4 py-3">
        <DefinitionRows
          items={[
            {
              term: st2('headline.read'),
              detail: <span data-numeric>{count(latest.n, getLocale())}</span>,
            },
            {
              term: st2('headline.period'),
              detail: (
                <span data-numeric>
                  {st2('headline.periodValue', {
                    from: day(props.window.from),
                    to: day(props.window.to),
                  })}
                </span>
              ),
            },
          ]}
        />
      </section>
      <EstimateLabel methodVersion={latest.version} confidence={null} />
    </SentimentCard>
  );
}
